import { Dayjs } from 'dayjs'
import { useShallow } from 'zustand/react/shallow'

import { day, getCalendarDates, isAfter, isBefore } from '~/astra'
import { getFlag, useFlag } from '~/hooks/useFlag'
import { DepartmentKey, SelectedSpecialties } from '~/store/slices/filterSlice'
import { StoreState, useStore } from '~/store/store'

import { Include, Practitioner, PractitionerServicePeriod, selectPractitioners, StormQueryOfPlural, usePractitioners } from '../'

export const PRACTITIONER_DIPS_RESOURCE_INCLUDE = {
    dipsResource: true,
} as const

export type PractitionerWithDipsResource = Include<Practitioner, typeof PRACTITIONER_DIPS_RESOURCE_INCLUDE>

/** Practitioner shape used in the booking form resource picker (DIPS resource + department sort order). */
export const BOOKING_PRACTITIONER_INCLUDE = {
    ...PRACTITIONER_DIPS_RESOURCE_INCLUDE,
    departmentAssignments: true,
} as const

export type BookingPractitioner = Include<Practitioner, typeof BOOKING_PRACTITIONER_INCLUDE>

export function selectPractitionersWithDipsResource(state: StoreState) {
    return selectPractitioners(state, { include: PRACTITIONER_DIPS_RESOURCE_INCLUDE })
}

export const HOSPITAL_CONFIG_PRACTITIONER_INCLUDE = {
    dipsResource: true,
    servicePeriods: true,
    departments: true,
    departmentAssignments: true,
    speciality: true,
    practitionerProfiles: {
        profileSurgeryTypeGroups: { surgeryTypeGroup: true },
    },
} as const

export type HospitalConfigPractitioner = Include<Practitioner, typeof HOSPITAL_CONFIG_PRACTITIONER_INCLUDE>

export function useAllPractitionersForHospitalConfig() {
    return usePractitioners({
        orderBy: ['short_name'],
        include: HOSPITAL_CONFIG_PRACTITIONER_INCLUDE,
    })
}

type PractitionerLikeForActivity = {
    servicePeriods: PractitionerServicePeriod[]
}

export function isPractitionerActive(practitioner: PractitionerLikeForActivity, monthStart?: Dayjs, monthEnd?: Dayjs) {
    if (practitioner.servicePeriods.length === 0) return true
    if (!monthStart || !monthEnd) return true

    return practitioner.servicePeriods.some(sp => {
        if (sp.start_time === sp.end_time) return true
        const start = sp.start_time ? day(sp.start_time) : undefined
        const end = sp.end_time ? day(sp.end_time) : undefined

        const startsBeforeMonthEnd = !start || !isAfter(start, monthEnd)
        const endsAfterMonthStart = !end || !isBefore(end, monthStart)

        return startsBeforeMonthEnd && endsAfterMonthStart
    })
}

export type UseActivePractitionersOptions = {
    departmentKey?: DepartmentKey
    selectedSpecialties?: SelectedSpecialties
    selectedDate?: Dayjs
}

type ActivePractitioner = Include<Practitioner, { servicePeriods: true; departmentAssignments: true }>

type PractitionerOrderLike = {
    short_name: string
    order?: number | null
    departmentAssignments: readonly { department_id: number; order?: number | null }[]
}

/**
 * Normalized scoping for the active-practitioner selectors. `queryWhere` narrows the practitioner query.
 * Behind `use_specialties_selection` we scope by the selected specialties and sort by `practitioner.order`
 * (`shouldUsePractitionerOrder`); otherwise we scope by the legacy department and sort by the assignment `order`
 * in `sortDepartmentIds`. With neither sort hint set the list falls back to `short_name`.
 */
type PractitionerWhere = NonNullable<StormQueryOfPlural<typeof usePractitioners>['where']>
type PractitionerQueryOptions = Omit<StormQueryOfPlural<typeof usePractitioners>, 'where'>

export type ActivePractitionersScope = {
    queryWhere: PractitionerWhere | undefined
    sortDepartmentIds?: ReadonlySet<number>
    shouldUsePractitionerOrder?: boolean
}

/** No query filter, alphabetical sort — the `'all'` selection in both scoping modes. */
const UNSCOPED: ActivePractitionersScope = { queryWhere: undefined }

function departmentScope(departmentKey: DepartmentKey): ActivePractitionersScope {
    if (departmentKey === 'all') return UNSCOPED
    return { queryWhere: { departmentAssignments: { any: { department_id: departmentKey } } }, sortDepartmentIds: new Set([departmentKey]) }
}

function specialtyScope(selectedSpecialties: SelectedSpecialties): ActivePractitionersScope {
    // An empty selection is treated like 'all': no specialty narrows the query, so every practitioner is returned.
    if (selectedSpecialties === 'all' || selectedSpecialties.length === 0) return UNSCOPED
    return { queryWhere: { speciality_uuid: { in: selectedSpecialties } }, shouldUsePractitionerOrder: true }
}

function resolveScope(
    isSpecialtiesSelectionEnabled: boolean,
    departmentKey: DepartmentKey,
    selectedSpecialties: SelectedSpecialties
): ActivePractitionersScope {
    return isSpecialtiesSelectionEnabled ? specialtyScope(selectedSpecialties) : departmentScope(departmentKey)
}

/**
 * In the flag-off path an explicit `departmentKey` option wins; otherwise `{ selectedSpecialties: 'all' }`
 * widens the scope to every department, falling back to the stored department key.
 */
function resolveDepartmentKey(storeDepartmentKey: DepartmentKey, options?: UseActivePractitionersOptions): DepartmentKey {
    if (options?.departmentKey !== undefined) return options.departmentKey
    return options?.selectedSpecialties === 'all' ? 'all' : storeDepartmentKey
}

function orderInDepartments(practitioner: PractitionerOrderLike, departmentIds: ReadonlySet<number>): number {
    let order = Infinity
    for (const assignment of practitioner.departmentAssignments) {
        if (departmentIds.has(assignment.department_id)) {
            order = Math.min(order, assignment.order ?? Infinity)
        }
    }
    return order
}

export function compareActivePractitionersOrder(scope: ActivePractitionersScope, a: PractitionerOrderLike, b: PractitionerOrderLike): number {
    if (scope.shouldUsePractitionerOrder) {
        const orderA = a.order ?? Infinity
        const orderB = b.order ?? Infinity
        // Names break ties, including the both-unordered case where subtracting `Infinity - Infinity` = NaN.
        return orderA === orderB ? a.short_name.localeCompare(b.short_name) : orderA - orderB
    }
    if (!scope.sortDepartmentIds) return a.short_name.localeCompare(b.short_name)

    const orderA = orderInDepartments(a, scope.sortDepartmentIds)
    const orderB = orderInDepartments(b, scope.sortDepartmentIds)
    // Equal orders cover the both-unordered case, where subtracting would yield `Infinity - Infinity` = NaN.
    // Names are the tie-break so unordered practitioners keep storm's default `short_name` order.
    return orderA === orderB ? a.short_name.localeCompare(b.short_name) : orderA - orderB
}

function filterAndSortActivePractitioners<T extends ActivePractitioner>(
    practitioners: readonly T[],
    scope: ActivePractitionersScope,
    selectedDate: Dayjs
): T[] {
    const daysOfMonth = getCalendarDates(selectedDate)
    const monthStart = daysOfMonth.at(0)
    const monthEnd = daysOfMonth.at(-1)

    const active = monthStart && monthEnd ? practitioners.filter(p => isPractitionerActive(p, monthStart, monthEnd)) : practitioners

    return active.toSorted((a, b) => compareActivePractitionersOrder(scope, a, b))
}

function getActivePractitionersQuery(scope: ActivePractitionersScope, attrs: PractitionerWhere | undefined, opts: PractitionerQueryOptions | undefined) {
    const mergedAttrs: PractitionerWhere | undefined = attrs && scope.queryWhere ? { AND: [attrs, scope.queryWhere] } : (attrs ?? scope.queryWhere)

    const includeOverrides = {
        servicePeriods: true,
        departmentAssignments: true,
    } satisfies NonNullable<PractitionerQueryOptions['include']>

    const mergedOpts = {
        ...(opts ?? {}),
        include: {
            ...opts?.include,
            ...includeOverrides,
        },
    }

    return { mergedAttrs, mergedOpts }
}

export function selectActivePractitioners<Opts extends PractitionerQueryOptions>(
    state: StoreState,
    options?: UseActivePractitionersOptions,
    attrs?: PractitionerWhere,
    opts?: Opts
) {
    const selectedDate = options?.selectedDate ?? state.appFilters.selectedDate
    const isSpecialtiesSelectionEnabled = getFlag('use_specialties_selection')
    const selectedSpecialties = options?.selectedSpecialties ?? state.appFilters.selectedSpecialties
    const scope = resolveScope(isSpecialtiesSelectionEnabled, resolveDepartmentKey(state.appFilters.departmentKey, options), selectedSpecialties)

    const { mergedAttrs, mergedOpts } = getActivePractitionersQuery(scope, attrs, opts)
    const rawPractitioners = selectPractitioners(state, { ...(mergedAttrs ? { where: mergedAttrs } : {}), ...mergedOpts })
    return filterAndSortActivePractitioners(rawPractitioners, scope, selectedDate)
}

export function useActivePractitionersScope(options?: Pick<UseActivePractitionersOptions, 'selectedSpecialties' | 'departmentKey'>): ActivePractitionersScope {
    const { storeDepartmentKey, storeSelectedSpecialties } = useStore(
        useShallow(state => ({
            storeDepartmentKey: state.appFilters.departmentKey,
            storeSelectedSpecialties: state.appFilters.selectedSpecialties,
        }))
    )
    const isSpecialtiesSelectionEnabled = useFlag('use_specialties_selection')
    const selectedSpecialties = options?.selectedSpecialties ?? storeSelectedSpecialties

    return resolveScope(isSpecialtiesSelectionEnabled, resolveDepartmentKey(storeDepartmentKey, options), selectedSpecialties)
}

/**
 * Hook to retrieve the active practitioners based on the current scope (department vs specialties),by their "order", and selected date.
 */
export function useActivePractitioners<Opts extends PractitionerQueryOptions>(options?: UseActivePractitionersOptions, attrs?: PractitionerWhere, opts?: Opts) {
    const scope = useActivePractitionersScope(options)
    const selectedDate = useStore(state => options?.selectedDate ?? state.appFilters.selectedDate)
    const { mergedAttrs, mergedOpts } = getActivePractitionersQuery(scope, attrs, opts)

    const rawPractitioners = usePractitioners({ where: mergedAttrs, ...mergedOpts })

    return filterAndSortActivePractitioners(rawPractitioners, scope, selectedDate)
}
